import React, { useEffect, useRef, useState } from 'react'

type Props = {
    placeholderWidth?: number
    placeholderHeight: number
    children: React.ReactNode
}

/**
 * Renders its children once the placeholder first scrolls into view, so a table of many charts mounts
 * only the ones the reader reaches. Where there is no IntersectionObserver (jsdom, very old browsers)
 * the children render straight away rather than never.
 */
/** Charts mount slightly before they scroll into view, so a fast scroll does not show empty cells. */
const PRELOAD_MARGIN = '100px'

export const LazyMount: React.FC<Props> = ({ placeholderWidth, placeholderHeight, children }) => {
    const placeholderRef = useRef<HTMLSpanElement>(null)
    const observerAvailable = typeof IntersectionObserver !== 'undefined'
    const [hasBeenVisible, setHasBeenVisible] = useState(!observerAvailable)

    useEffect(() => {
        const placeholder = placeholderRef.current
        if (hasBeenVisible || !placeholder) return
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setHasBeenVisible(true)
                    observer.disconnect()
                }
            },
            { rootMargin: PRELOAD_MARGIN },
        )
        observer.observe(placeholder)
        return () => observer.disconnect()
    }, [hasBeenVisible])

    if (hasBeenVisible) return <>{children}</>
    return (
        <span
            ref={placeholderRef}
            aria-hidden="true"
            style={{ display: 'inline-block', width: placeholderWidth ?? '100%', height: placeholderHeight }}
        />
    )
}
